<template>
  <div class="tw:!space-y-4">
    <fieldset>
      <legend>Card Number</legend>
      <div class="od-input tw:w-full" :ref="element.number" />
    </fieldset>

    <div class="tw:grid tw:grid-cols-2 tw:gap-4">
      <fieldset>
        <legend>Expiration (MM/YY)</legend>
        <div class="od-input tw:w-full" :ref="element.expiry" />
      </fieldset>

      <fieldset>
        <legend>CVV</legend>
        <div class="od-input tw:w-full" :ref="element.cvc" />
      </fieldset>
    </div>

    <fieldset>
      <legend>Card holder name</legend>
      <input class="od-input tw:w-full" v-model="holderName" placeholder="Name of credit card" />
    </fieldset>

    <div v-if="error" class="tw:!text-sm tw:!mt-2 tw:!text-red-500">{{ error }}</div>

    <div class="tw:!flex tw:!items-center tw:!justify-center tw:!gap-2">
      <ShieldCheck class="tw:!w-4 tw:!h-4 tw:!text-gray-500" />
      <span class="tw:!text-xs tw:!text-gray-500">
        {{ $t('checkout.payment.secureEncrypted') }}
      </span>
    </div>

    <button class="od-button tw:!w-full tw:!mt-1 tw:!font-semibold" @click="payment" :disabled="loading">
      <span v-if="loading">Processing...</span>
      <span v-else>Pay Now ${{ formatPrice(props.amount) }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { StripeCardNumberElement, StripeCardExpiryElement, StripeCardCvcElement, PaymentIntent, Stripe } from '@stripe/stripe-js'
import { onMounted, ref } from 'vue'
import { useStripe } from '~/composables/usePayment'
import { formatPrice } from '~/utility/helper'
import { ShieldCheck } from 'lucide-vue-next'

const props = defineProps<{
  clientSecret: string
  amount: number
  email?: string
}>()

const emit = defineEmits<{
  success: [paymentIntent: PaymentIntent]
}>()

const holderName = ref('')
const loading = ref(false)
const error = ref('')

let stripe: Stripe | null = null
let card: {
  number: StripeCardNumberElement
  expiry: StripeCardExpiryElement
  cvc: StripeCardCvcElement
}

const element = {
  number: ref<HTMLElement>(),
  expiry: ref<HTMLElement>(),
  cvc: ref<HTMLElement>()
}

onMounted(async () => {
  stripe = await useStripe()

  const elements = stripe!.elements()

  const style = {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#9e2146' },
  }

  card = {
    number: elements.create('cardNumber', { style }),
    expiry: elements.create('cardExpiry', { style }),
    cvc: elements.create('cardCvc', { style }),
  }

  for (const key in card) {
    const i = key as 'number' | 'expiry' | 'cvc'
    card[i].mount(element[i].value!)
    // @ts-ignore
    card[i].on('change', event => error.value = event.error?.message || '')
  }
})

const payment = async () => {
  if (!holderName.value.trim()) {
    return error.value = 'Please enter cardholder name'
  }

  if (loading.value) return

  error.value = ''
  loading.value = true

  return await stripe!.confirmCardPayment(props.clientSecret, {
    payment_method: {
      card: card.number,
      billing_details: {
        name: holderName.value.trim(),
        email: props.email
      },
    },
  })
    .then(({ paymentIntent, error }) => {
      if (error) throw error
      paymentIntent?.status === 'succeeded' && emit('success', paymentIntent)
    })
    .catch((e: Error) => error.value = e.message || 'Payment failed')
    .finally(() => loading.value = false)
}
</script>
